import { baseApi, QueryPayload } from 'src/api'
import { map } from 'lodash'

export enum TransactionType {
    Credit = 'credit',
    Debit = 'debit',
    Refund = 'refund',
}

export type BalanceTransaction = {
    id: string,
    type: {
        key: TransactionType,
        name: string,
        namePlural: string
    },
    amount: number;
    description: string;
    createdAt: number;
};

const domainApiModule = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getStudentCoinBalance: builder.query<number, { params: { studentId: number } }>({
            providesTags: (_r: number | undefined, _e: unknown, { params }) =>
                [{ type: 'CoinBalance', id: params.studentId }],
            query: ({ params }): QueryPayload => ({
                url: `/students/${params.studentId}/coins/`,
                method: 'GET',
            }),
            transformResponse: (res: string) => Number.parseFloat(res),
        }),
        
        getCurrentStudentCoinBalance: builder.query<number, void>({
            providesTags: ['CoinBalance'],
            query: (): QueryPayload => ({
                url: `/students/me/coins/`,
                method: 'GET',
            }),
            transformResponse: (res: string) => Number.parseFloat(res),
        }),
        
        getStudentCoinBalanceTransactions: builder.query<BalanceTransaction[], { params: { studentId: number } }>({
            providesTags: (_r, _e, { params }) =>
                [{ type: 'CoinBalanceTransaction', id: params.studentId }],
            query: ({ params }): QueryPayload => ({
                url: `/students/${params.studentId}/coins/transactions`,
                method: 'GET',
            }),
            transformResponse: (res: any[]) => map(res, (t) => ({ ...t, amount: Number.parseFloat(t.amount) })),
        }),
        
        debitStudentCoinBalance: builder.mutation<void, {
            params: { studentId: number };
            body: { amount: number, description: string }
        }>({
            invalidatesTags: (_r, _e, { params }) => [{
                type: 'CoinBalance',
                id: params.studentId,
            }, { type: 'CoinBalanceTransaction', id: params.studentId }],
            query: ({ params, body }): QueryPayload => ({
                url: `/students/${params.studentId}/coins/debit`,
                method: 'POST',
                data: body,
            }),
        }),
        
        creditStudentCoinBalance: builder.mutation<void, {
            params: { studentId: number };
            body: { amount: number, description: string }
        }>({
            invalidatesTags: (_r, _e, { params }) => [{
                type: 'CoinBalance',
                id: params.studentId,
            }, { type: 'CoinBalanceTransaction', id: params.studentId }],
            query: ({ params, body }): QueryPayload => ({
                url: `/students/${params.studentId}/coins/credit`,
                method: 'POST',
                data: body,
            }),
        }),
    }),
})

export const {
    useGetStudentCoinBalanceQuery,
    useGetStudentCoinBalanceTransactionsQuery,
    useDebitStudentCoinBalanceMutation,
    useGetCurrentStudentCoinBalanceQuery,
    useCreditStudentCoinBalanceMutation,
} = domainApiModule